import React, { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { T } from "../../app/tokens.js";
import { Popover } from "../../components/Popover.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "../../features/reports/reportNavEligibility.js";
import { getHomeFreshness } from "../../data/reportsClient.js";
import { viewToPath } from "../../services/buildUrl.js";
import { coverageFeeds, evidenceValue, freshnessSummary, GOVERNING_FEEDS, OTHER_FEEDS, localGreeting, timestampAge } from "./homeHeaderFormat.js";
import "./homeHeader.css";

const colors = { current: T.ragGreenText, stale: T.ragAmberText, error: T.ragRed, unavailable: T.mid };
const toneFor = (status) => status === "Error" ? "error" : status === "Current" ? "current" : "stale";

function SourceRow({ feed, now }) {
  const successAge = timestampAge(feed.lastSuccessAt, now);
  return (
    <div className="home-freshness-source" data-testid={`home-source-${feed.id}`}>
      <div className="home-freshness-source-title">
        <strong>{feed.label}</strong>
        <span style={{ color: colors[toneFor(feed.status)], fontWeight: T.fwSemibold }}>{feed.status}</span>
      </div>
      <div>{feed.source} · {feed.cadence}</div>
      <div>Last successful ingestion: {successAge ? `synced ${successAge}` : "Unavailable"}</div>
      {feed.lastSuccessAt && <div>{evidenceValue(feed.lastSuccessAt, now)}</div>}
      <div>Last attempt: {evidenceValue(feed.lastAttemptAt, now)}</div>
      {feed.evidence.map((item, index) => <div key={index}>{item.label}: {evidenceValue(item.value, now)}</div>)}
      <p>{feed.notes}</p>
    </div>
  );
}

// This child is remounted for each identity/permission scope. Its cleanup also
// discards in-flight responses, including when access is removed.
function AuthorizedFreshness({ now }) {
  const [state, setState] = useState({ status: "loading", feeds: null });
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const skipTriggerClick = useRef(false);
  const id = useId();
  useEffect(() => {
    let live = true;
    let generation = 0;
    const refresh = () => {
      const request = ++generation;
      setState({ status: "loading", feeds: null });
      Promise.resolve().then(() => {
        if (!live || request !== generation) return null;
        return getHomeFreshness();
      }).then((data) => {
        if (!live || request !== generation) return;
        const feeds = coverageFeeds(data);
        setState({ status: feeds ? "ready" : "unavailable", feeds });
      }).catch(() => {
        if (live && request === generation) setState({ status: "unavailable", feeds: null });
      });
    };
    const visible = () => { if (document.visibilityState === "visible") refresh(); };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", visible);
    return () => {
      live = false;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, []);
  useEffect(() => {
    if (open) document.getElementById(id)?.focus();
  }, [open, id]);
  const summary = state.status === "ready" ? freshnessSummary(state.feeds) : null;
  const label = summary?.label ?? (state.status === "loading" ? "Checking data…" : "Data unavailable");
  const close = () => {
    setOpen(false);
    // Return keyboard focus, but do not steal focus from an outside click.
    if (document.getElementById(id)?.contains(document.activeElement)) triggerRef.current?.focus();
  };
  return (
    <>
      <button type="button" className="home-freshness-chip" data-testid="home-freshness-chip"
        ref={triggerRef} aria-label={label} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
        onMouseDown={() => { skipTriggerClick.current = open; }}
        onClick={() => {
          if (skipTriggerClick.current) { skipTriggerClick.current = false; return; }
          setOpen((value) => !value);
        }}>
        <span className="home-freshness-dot" style={{ background: colors[summary?.tone ?? "unavailable"] }} />
        <span aria-live="polite">{label}</span><ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && <Popover id={id} triggerRef={triggerRef} onClose={close} variant="menu" portalToBody
        horizontalViewportInset={16} aria-label="Data source freshness" data-testid="home-freshness-popover" tabIndex={-1}
        style={{ width: 420, padding: "14px 16px" }}>
        <div className="home-freshness-content">
          <h2>Data sources</h2>
          {state.feeds ? <>
            {GOVERNING_FEEDS.map((key) => <SourceRow key={key} feed={state.feeds.find((feed) => feed.id === key)} now={now} />)}
            <h2>Other sources</h2>
            <p>Shown for context; these sources do not determine the chip status.</p>
            {OTHER_FEEDS.map((key) => <SourceRow key={key} feed={state.feeds.find((feed) => feed.id === key)} now={now} />)}
          </> : <p role="status">{state.status === "loading" ? "Loading source evidence…" : "Source freshness is unavailable. No current status can be verified."}</p>}
          <Link className="home-freshness-link" to={viewToPath("report-data-coverage")} onClick={() => setOpen(false)}>Open Data coverage →</Link>
        </div>
      </Popover>}
    </>
  );
}

export function HomeHeader({ currentUser, currentRole, actualRole }) {
  const context = useFeatureFlagContext() ?? {};
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const refresh = () => setNow(new Date());
    const visible = () => { if (document.visibilityState === "visible") refresh(); };
    const interval = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, []);
  const identity = currentUser?.id ?? currentUser?.okta_sub ?? currentUser?.email;
  // Admin-only 2026-11-01 (Corey). Future: per-user 'things to be aware of' chip driven by the user's role/view (tasks, decisions), not data freshness.
  const allowed = currentRole === "admin" && Boolean(identity) && context.flagsReady === true
    && context.reportFlags?.some((flag) => flag.key === "report-data-coverage" && flag.enabled === true)
    && getReportNavEligibility(context, currentRole).dataCoverage === true;
  const scope = JSON.stringify([identity, currentUser?.id, currentUser?.okta_sub, currentUser?.email,
    currentUser?.role, currentUser?.devActAs, actualRole, currentRole, context.flagsReady, context.reportAccess,
    context.reportFlags, context.flags, context.nav_config, context.nav_order]);
  const { greeting, date } = localGreeting(currentUser?.display_name, now);
  return (
    <div className="home-header" data-testid="home-header">
      <h1 data-testid="home-greeting">{greeting}</h1>
      <span className="home-header-date" data-testid="home-local-date">{date}</span>
      {allowed && <AuthorizedFreshness key={scope} now={now} />}
    </div>
  );
}
